import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Progress } from '../../components/ui/progress';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Award,
  DollarSign,
  Loader2,
  CheckCircle2,
  ChevronRight,
  ChevronLeft,
  Dumbbell,
  Globe,
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sports list
const SPORTS_LIST = [
  'Cricket', 'Football', 'Tennis', 'Badminton', 'Swimming',
  'Basketball', 'Volleyball', 'Athletics', 'Hockey', 'Rugby',
  'Golf', 'Squash', 'Table Tennis', 'Boxing', 'MMA',
  'Yoga', 'Pilates', 'Cycling', 'Running', 'Triathlon',
  'Weightlifting', 'CrossFit', 'Gymnastics', 'Martial Arts', 'Other'
];

// Subcategories
const SUBCATEGORIES = [
  { id: 'sports_equipment', name: 'Sports Equipment' },
  { id: 'fitness_gym', name: 'Fitness & Gym' },
  { id: 'outdoor_adventure', name: 'Outdoor & Adventure' },
  { id: 'cycling', name: 'Cycling' },
  { id: 'water_sports', name: 'Water Sports' },
];

const CoachRegister = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    // Step 1: Basic Info
    name: '',
    email: '',
    phone: '',
    country: 'SL',
    city: '',
    // Step 2: Experience
    experience_years: 0,
    subcategories: [],
    specializations: [],
    certifications: [],
    bio: '',
    // Step 3: Pricing
    individual_rate: 0,
    group_rate: 0,
    online_rate: 0,
  });

  const [newCert, setNewCert] = useState('');

  const totalSteps = 3;

  const addSpecialization = (sport) => {
    if (sport && !form.specializations.includes(sport)) {
      setForm(prev => ({
        ...prev,
        specializations: [...prev.specializations, sport]
      }));
    }
  };

  const removeSpecialization = (sport) => {
    setForm(prev => ({
      ...prev,
      specializations: prev.specializations.filter(s => s !== sport)
    }));
  };

  const addCertification = () => {
    if (newCert.trim() && !form.certifications.includes(newCert.trim())) {
      setForm(prev => ({
        ...prev,
        certifications: [...prev.certifications, newCert.trim()]
      }));
      setNewCert('');
    }
  };

  const removeCertification = (cert) => {
    setForm(prev => ({
      ...prev,
      certifications: prev.certifications.filter(c => c !== cert)
    }));
  };

  const toggleSubcategory = (subcatId) => {
    setForm(prev => ({
      ...prev,
      subcategories: prev.subcategories.includes(subcatId)
        ? prev.subcategories.filter(s => s !== subcatId)
        : [...prev.subcategories, subcatId]
    }));
  };

  const validateStep = () => {
    switch (step) {
      case 1:
        if (!form.name || !form.email || !form.phone || !form.city) {
          setError('Please fill in all required fields');
          return false;
        }
        if (!/\S+@\S+\.\S+/.test(form.email)) {
          setError('Please enter a valid email address');
          return false;
        }
        break;
      case 2:
        if (form.specializations.length === 0) {
          setError('Please select at least one specialization');
          return false;
        }
        if (form.subcategories.length === 0) {
          setError('Please select at least one category');
          return false;
        }
        break;
      case 3:
        if (form.individual_rate <= 0 && form.group_rate <= 0 && form.online_rate <= 0) {
          setError('Please set at least one pricing rate');
          return false;
        }
        break;
    }
    setError('');
    return true;
  };

  const handleNext = () => {
    if (validateStep()) {
      setStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const handleBack = () => {
    setStep(prev => Math.max(prev - 1, 1));
    setError('');
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_URL}/api/sports-services/coach/register`, form);
      
      // Store coach ID for login
      localStorage.setItem('coach_id', response.data.coach_id);
      
      // Navigate to dashboard
      navigate('/coach/dashboard');
    } catch (err) {
      console.error('Registration failed:', err);
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Check if already logged in
  const existingCoachId = localStorage.getItem('coach_id');
  if (existingCoachId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold">Already Registered</h2>
            <p className="text-gray-500 mt-2">You are already registered as a coach.</p>
            <div className="mt-4 flex gap-3 justify-center">
              <Button onClick={() => navigate('/coach/dashboard')}>
                Go to Dashboard
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  localStorage.removeItem('coach_id');
                  window.location.reload();
                }}
              >
                Register New Account
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-12 px-4" data-testid="coach-register">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Dumbbell className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Become a Coach</h1>
          <p className="text-gray-600 mt-2">Join our network of verified sports coaches</p>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-gray-600">Step {step} of {totalSteps}</span>
            <span className="text-gray-600">{Math.round((step / totalSteps) * 100)}% complete</span>
          </div>
          <Progress value={(step / totalSteps) * 100} className="h-2" />
        </div>

        {/* Form Card */}
        <Card>
          <CardHeader>
            <CardTitle>
              {step === 1 && 'Personal Information'}
              {step === 2 && 'Experience & Specializations'}
              {step === 3 && 'Pricing'}
            </CardTitle>
            <CardDescription>
              {step === 1 && 'Tell us about yourself'}
              {step === 2 && 'Share your coaching expertise'}
              {step === 3 && 'Set your session rates'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Step 1: Basic Info */}
            {step === 1 && (
              <div className="space-y-4">
                <div>
                  <Label>Full Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input 
                      value={form.name}
                      onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                      className="pl-10"
                      placeholder="John Silva"
                    />
                  </div>
                </div>

                <div>
                  <Label>Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input 
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                      className="pl-10"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div>
                  <Label>Phone *</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input 
                      value={form.phone}
                      onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
                      className="pl-10"
                      placeholder="+94 77 123 4567"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Country *</Label>
                    <Select 
                      value={form.country}
                      onValueChange={(v) => setForm(prev => ({ ...prev, country: v }))}
                    >
                      <SelectTrigger>
                        <Globe className="h-4 w-4 mr-2" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SL">🇱🇰 Sri Lanka</SelectItem>
                        <SelectItem value="UK">🇬🇧 United Kingdom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>City *</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input 
                        value={form.city}
                        onChange={(e) => setForm(prev => ({ ...prev, city: e.target.value }))}
                        className="pl-10"
                        placeholder="Colombo"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Experience */}
            {step === 2 && (
              <div className="space-y-6">
                <div>
                  <Label>Years of Experience</Label>
                  <Input 
                    type="number"
                    value={form.experience_years}
                    onChange={(e) => setForm(prev => ({ ...prev, experience_years: parseInt(e.target.value) || 0 }))}
                    min={0}
                    max={50}
                  />
                </div>

                <div>
                  <Label>Categories You Coach In *</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {SUBCATEGORIES.map(cat => (
                      <Button
                        key={cat.id}
                        type="button"
                        variant={form.subcategories.includes(cat.id) ? 'default' : 'outline'}
                        className="justify-start"
                        onClick={() => toggleSubcategory(cat.id)}
                      >
                        {form.subcategories.includes(cat.id) && <CheckCircle2 className="h-4 w-4 mr-2" />}
                        {cat.name}
                      </Button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Sports Specializations *</Label>
                  <div className="flex flex-wrap gap-2 mt-2 mb-3">
                    {form.specializations.map(sport => (
                      <Badge 
                        key={sport} 
                        variant="secondary"
                        className="cursor-pointer hover:bg-red-100"
                        onClick={() => removeSpecialization(sport)}
                      >
                        {sport} ×
                      </Badge>
                    ))}
                  </div>
                  <Select onValueChange={addSpecialization}>
                    <SelectTrigger>
                      <SelectValue placeholder="Add a sport..." />
                    </SelectTrigger>
                    <SelectContent>
                      {SPORTS_LIST.filter(s => !form.specializations.includes(s)).map(sport => (
                        <SelectItem key={sport} value={sport}>{sport}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Certifications</Label>
                  <div className="flex flex-wrap gap-2 mt-2 mb-3">
                    {form.certifications.map(cert => (
                      <Badge 
                        key={cert} 
                        variant="outline"
                        className="cursor-pointer hover:bg-red-100"
                        onClick={() => removeCertification(cert)}
                      >
                        <Award className="h-3 w-3 mr-1" />
                        {cert} ×
                      </Badge>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input 
                      value={newCert}
                      onChange={(e) => setNewCert(e.target.value)}
                      placeholder="e.g., ACE Certified Personal Trainer"
                      onKeyPress={(e) => e.key === 'Enter' && addCertification()}
                    />
                    <Button type="button" onClick={addCertification}>Add</Button>
                  </div>
                </div>

                <div>
                  <Label>Bio</Label>
                  <Textarea 
                    value={form.bio}
                    onChange={(e) => setForm(prev => ({ ...prev, bio: e.target.value }))}
                    placeholder="Tell potential clients about your coaching experience, philosophy, and achievements..."
                    rows={4}
                  />
                </div>
              </div>
            )}

            {/* Step 3: Pricing */}
            {step === 3 && (
              <div className="space-y-6">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">Set Your Session Rates</h4>
                  <p className="text-sm text-blue-700">
                    Set competitive rates based on your experience. You can always update these later.
                  </p>
                </div>

                <div className="grid gap-4">
                  <div>
                    <Label>Individual Session Rate ({form.country === 'UK' ? '£' : 'Rs'}) *</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input 
                        type="number"
                        value={form.individual_rate}
                        onChange={(e) => setForm(prev => ({ ...prev, individual_rate: parseInt(e.target.value) || 0 }))}
                        className="pl-10"
                        placeholder="0"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Price per 1-hour individual session</p>
                  </div>

                  <div>
                    <Label>Group Session Rate ({form.country === 'UK' ? '£' : 'Rs'})</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input 
                        type="number"
                        value={form.group_rate}
                        onChange={(e) => setForm(prev => ({ ...prev, group_rate: parseInt(e.target.value) || 0 }))}
                        className="pl-10"
                        placeholder="0"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Price per person for group sessions</p>
                  </div>

                  <div>
                    <Label>Online Session Rate ({form.country === 'UK' ? '£' : 'Rs'})</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input 
                        type="number"
                        value={form.online_rate}
                        onChange={(e) => setForm(prev => ({ ...prev, online_rate: parseInt(e.target.value) || 0 }))}
                        className="pl-10"
                        placeholder="0"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Price per 1-hour online coaching session</p>
                  </div>
                </div>

                {/* Summary */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium mb-3">Registration Summary</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Name:</span>
                      <span className="font-medium">{form.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Location:</span>
                      <span className="font-medium">{form.city}, {form.country}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Specializations:</span>
                      <span className="font-medium">{form.specializations.length} sports</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Experience:</span>
                      <span className="font-medium">{form.experience_years} years</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>

          {/* Navigation */}
          <div className="px-6 pb-6 flex justify-between">
            <Button 
              variant="outline" 
              onClick={handleBack}
              disabled={step === 1}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            
            {step < totalSteps ? (
              <Button onClick={handleNext}>
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Registering...
                  </>
                ) : (
                  <>
                    Complete Registration
                    <CheckCircle2 className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            )}
          </div>
        </Card>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-6">
          By registering, you agree to our Terms of Service and Privacy Policy.
        </p>
      </div>
    </div>
  );
};

export default CoachRegister;
